import { ContentfulFilters, FilterList, IArticle, TagSubtopic, TagTopic } from '../components/pageBuilder/Sections/SectionFilterableArticles/types/SectionFilterableArticles.types';

export function compareByFeatured(a: IArticle, b: IArticle) {
  if (a.featured && !b.featured) {
    return -1;
  }

  if (!a.featured && b.featured) {
    return 1;
  }
  return 0;
}

export function compareArticlesByViewCount(a: IArticle, b: IArticle) {
  return b.viewCount - a.viewCount;
}

export function compareTopicsByViewCount(a: TagTopic, b: TagTopic) {
  return b.viewCount - a.viewCount;
}

export function clearDisplayAsFeatured(articles: IArticle[]) {
  articles.forEach((article: IArticle) => {
    article.displayAsFeatured = false;
  });
  return articles;
}

export function compareByName(a: IArticle, b: IArticle) {
  if (a.pageArticleName.toUpperCase() < b.pageArticleName.toUpperCase()) {
    return -1;
  }
  if (a.pageArticleName.toUpperCase() > b.pageArticleName.toUpperCase()) {
    return 1;
  }
  return 0;
}

export function checkAppliedDrawerFilters(filterList: FilterList) {
  return (
    filterList.selectedSubtopics.length +
    filterList.contentType.length +
    filterList.timeRange.length
  );
};

export function checkAppliedFilters(filterList: FilterList, searchQuery: string) {
  if (
    filterList.topic ||
    filterList.selectedTopics.length > 0 ||
    filterList.selectedSubtopics.length > 0 ||
    filterList.contentType.length > 0 ||
    filterList.timeRange.length > 0 ||
    filterList.latest ||
    filterList.default ||
    filterList.mostPopular ||
    filterList.recentlyViewed ||
    filterList.page ||
    searchQuery.length > 0
  ) {
    return true;
  } else {
    return false;
  }
};

export function updateUrlQueryParams(
  name: string,
  value: string | number,
  shouldDeleteFromUrl?: boolean
) {
  const url = new URL(window.location.href);
  if (value && !shouldDeleteFromUrl) {
    url.searchParams.set(name, value.toString());
  } else {
    url.searchParams.delete(name);
  }

  if (
    window.history.state?.data !== url &&
    url.href !== window.location.href
  ) {
    window.history.pushState(
      { action: 'filterChange', data: url.href },
      '',
      url
    );
  }

  return url;
};

export function getTagNameFromValue(
  tagList: TagTopic[] | TagSubtopic[],
  tagValue: string
): string {
  const tagValueArray = tagValue.split(',');

  const tagNameArray: string[] = [];

  if (tagValue === '') {
    return '';
  }

  tagValueArray.map(value => {
    tagList.some(tag => {
      if (value.includes(tag.slugName)) {
        tagNameArray.push(tag.displayName);
        return true;
      }
    });
  });

  return tagNameArray.join(',');
}

export function getTagsAndFiltersFromValues<T extends ContentfulFilters[number]>(
  tagList: T[],
  tagValues: string[]
): T[] {
  const tagArray: T[] = [];

  if (!tagList) {
    return [];
  }

  if (tagValues.length === 0 || tagList.length === 0) {
    return [];
  }

  tagValues.map(value => {
    tagList.some(tag => {
      if (value.includes(tag.slugName)) {
        tagArray.push(tag);
        return true;
      }
    });
  });
  return tagArray;
}

export function formFilteredContent(
  articles: IArticle[],
  filters: FilterList,
  query: string,
  recentlyViewedArticles: string[]
) {
  const topicFilter = filters.topic;
  const subtopicFilter = filters.subtopic;
  const timeRangeFilter = filters.timeRange;
  const contentTypeFilter = filters.contentType;

  let filteredData: IArticle[] = articles.filter(article => {
    let hasTopicMatch = false;
    let hasSubtopicMatch = false;
    let hasTimeRangeMatch = false;
    let hasContentTypeMatch = false;
    let hasTitleMatch = false;
    let hasSummaryMatch = false;
    let hasKeywordMatch = false;

    if (topicFilter.length !== 0) {
      hasTopicMatch = article.topics.some(
        tag =>
          topicFilter.includes(tag.displayName) ||
          topicFilter.includes(tag.name)
      );
    } else {
      hasTopicMatch = true;
    }

    if (subtopicFilter.length !== 0) {
      if (article.subtopics) {
        hasSubtopicMatch = article.subtopics.some(subtopic => {
          return subtopicFilter.includes(subtopic.displayName || subtopic.name);
        });
      } else {
        hasSubtopicMatch = false;
      }
    } else {
      hasSubtopicMatch = true;
    }

    if (timeRangeFilter.length !== 0) {
      const currentTime = new Date();
      hasTimeRangeMatch = timeRangeFilter.some(timeRange => {
        const timeThreshold =
          currentTime.getTime() - timeRange.timeRangeInMinutes * 60 * 1000;
        const articleTime = new Date(article.publishingDate).getTime();
        return articleTime >= timeThreshold;
      });
    } else {
      hasTimeRangeMatch = true;
    }

    if (contentTypeFilter.length !== 0) {
      hasContentTypeMatch = contentTypeFilter.some(contentTypeFilter => {
        const articleHasMatchingContentType =
          article.contentType?.slugName === contentTypeFilter.slugName;
        const articleHasNoContentTypeAndIsDefaultSelected =
          !article.contentType && contentTypeFilter.isDefault;

        return (
          articleHasMatchingContentType ||
          articleHasNoContentTypeAndIsDefaultSelected
        );
      });
    } else {
      hasContentTypeMatch = true;
    }

    if (query.length > 0) {
      hasTitleMatch = article.pageArticleName
        .toLowerCase()
        .includes(query.toLowerCase());

      hasSummaryMatch = article.summary
        .toLowerCase()
        .includes(query.toLowerCase());

      hasKeywordMatch = article.keywords
        .map(keyword => keyword.toLowerCase())
        .includes(query.toLowerCase());
    } else {
      hasTitleMatch = true;
      hasKeywordMatch = true;
    }

    return (
      hasTopicMatch &&
      hasSubtopicMatch &&
      hasTimeRangeMatch &&
      hasContentTypeMatch &&
      (hasTitleMatch || hasSummaryMatch || hasKeywordMatch)
    );
  });

  filteredData = clearDisplayAsFeatured(filteredData);

  if (filters.recentlyViewed) {
    filteredData = recentlyViewedArticles
      .map((slug: string) =>
        filteredData.find(article => article.slug === slug)
      )
      .filter((article: IArticle | undefined) => article !== undefined);
  }

  if (filters.media) {
    filteredData = filteredData.filter(article => {
      return article.contentType?.isMedia === true;
    });
  }

  if (filters.mostPopular) {
    filteredData = filteredData.sort(compareArticlesByViewCount);
    if (filteredData[0]) {
      filteredData[0].displayAsFeatured = true;
    }
  } else if (filters.latest) {
    filteredData = filteredData.sort((a, b) => {
      return (
        new Date(b.publishingDate).getTime() -
        new Date(a.publishingDate).getTime()
      );
    });
  } else if (filters.default) {
    filteredData = filteredData.sort(compareByName);
  } else {
    filteredData = filteredData.sort(compareByFeatured);
    if (filteredData[0] && filteredData[0].featured) {
      filteredData[0].displayAsFeatured = true;
    }
  }
  return filteredData;
}

export function getSubTopicsFromTopics(topics: TagTopic[]) {
  const subtopics: TagSubtopic[] = [];

  topics &&
    topics.some(topic => {
      if (topic.subtopics) {
        topic.subtopics.some(subtopic => {
          subtopics.push(subtopic);
        });
      }
    });
  return subtopics;
}

export function checkQuery(query: string, type: string) {
  return query === type;
};

export function getSubtopicNamesFromDisplayName(
  topics: TagTopic[],
  subTopicValue: string
) {
  const subTopicValueArray = subTopicValue.split(',');

  const subTopicNameArray: string[] = [];

  if (subTopicValue === '') {
    return '';
  }

  subTopicValueArray.map(value => {
    topics.some(topic => {
      if (topic.subtopics) {
        topic.subtopics.some(subtopic => {
          if (value.includes(subtopic.slugName)) {
            subTopicNameArray.push(subtopic.displayName);
          }
        });
      }
    });
  });

  return subTopicNameArray.join(',');
}